import {
	Session,
	useCrestaEventManager,
	useInitialReferrer,
	useSalesforceMCP,
} from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { useMarketingSource } from '@aspendental/web-shared-utils';
import * as Sentry from '@sentry/nextjs';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import React, { useEffect } from 'react';

import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { VWOSmartCode } from '@src/components/shared/third-party/analytics-vwo/VWOSmartCode';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import '@aspendental/shared-components-web/lib/dynamic-form.css';
import '@aspendental/shared-components-web/lib/fonts.css';
import '@aspendental/shared-components-web/lib/global.css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import { initializeLogRocket } from '@src/pages/utils/logRocketInit';
import { initializeSentryClient } from '@src/pages/utils/sentryClientInit';
import {
	hasSettledVerification,
	readVerificationState,
	verifyBrazeLocation,
	writeVerificationState,
} from '@src/pages/utils/verify-braze-location';
import { toggleLogRocket, isLogRocketEnabled } from '@src/services/logRocket';
import { toggleSentry, isSentryEnabled } from '@src/services/sentry';
export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
	const logger = getLogger('client');
	logger.info({
		name,
		category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
		value: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
		id, // id unique to current page load
	});
}

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
	const router = useRouter();
	const config = pageProps?.config as BrandThemeConfig;
	const { features, featureFlags } = config ?? {};
	const brandName = config?.name || 'Aspen Dental';
	const isClearChoiceBrand = brandName === 'ClearChoice';
	const brazeEnabled = features?.analytics?.braze?.enabled;
	const brazeSdkKey = features?.analytics?.braze?.sdkKey;
	const brazeBaseUrl = features?.analytics?.braze?.baseUrl;
	const brazeLoggingEnabled = features?.analytics?.braze?.enableLogging;
	const environment = pageProps?.env?.ENVIRONMENT_NAME || 'prod';
	toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
	toggleLogRocket(!!features?.logRocket?.enabled);

	// regular page and blog page
	const disableStickyFooterOnMobile =
		pageProps?.page?.disableStickyFooterOnMobile || pageProps?.pageData?.disableStickyFooterOnMobile;
	// regular page and blog page
	const disableStickyFooterOnDesktop =
		pageProps?.page?.disableStickyFooterOnDesktop || pageProps?.pageData?.disableStickyFooterOnDesktop;

	const disableHeaderTopAlertBanner =
		pageProps?.page?.disableHeaderTopAlertBanner || pageProps?.pageData?.disableHeaderTopAlertBanner;
	const disableHeaderBottomAlertBanner =
		pageProps?.page?.disableHeaderBottomAlertBanner || pageProps?.pageData?.disableHeaderBottomAlertBanner;

	const envSpecificSiteURL = (environment === 'prod' ? config?.siteURL : config?.siteURLStage) ?? '/';
	// setting logo href to absolute for full page load so web gateway will serve page from aspen-marketing app
	const logoHref = brandName === 'Aspen Dental' ? envSpecificSiteURL : '/';
	const showAspen2Header = featureFlags?.featureList?.includes('showAspen2Header') ?? false;
	const showClearChoice2Header = featureFlags?.featureList?.includes('showClearChoice2Header') ?? false;
	const hideNavigation = pageProps?.hideNavigation ?? false;
	// VWO `ccPatientPrismPhoneSwap` 1% treatment, resolved server-side via
	// resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...) and forwarded as
	// serverFeatureFlags. Treatment == flag enabled AND bucketed into a
	// non-control variation (variationId > 1). Variation 1 is always the
	// control/default, so `isEnabled` alone is true for the whole experiment
	// audience (both arms) — gating on it would swap for ~100% instead of 1%.
	// Mirrors getServerFlagVariation's `isVariation` gate in clearchoice-marketing.
	// Defaults to off (control) on any route that doesn't wire it up or on error.
	// Threaded into Layout to swap the national header CTA number.
	const ccPhoneSwapFlag = pageProps?.serverFeatureFlags?.ccPatientPrismPhoneSwap;
	const phoneSwapOn =
		(ccPhoneSwapFlag?.isEnabled ?? false) &&
		ccPhoneSwapFlag?.variationId != null &&
		Number(ccPhoneSwapFlag.variationId) > 1;

	// VWO `ghostPrimaryScheduleButton` (TTB-322) treatment, resolved server-side
	// via resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...) and forwarded as
	// serverFeatureFlags. Same non-control gate as phoneSwapOn (variationId > 1).
	// When on, the ClearChoice2 header renders its primary CTA as a ghost button.
	const ghostScheduleFlag = pageProps?.serverFeatureFlags?.ghostPrimaryScheduleButton;
	const ghostPrimaryScheduleButton =
		(ghostScheduleFlag?.isEnabled ?? false) &&
		ghostScheduleFlag?.variationId != null &&
		Number(ghostScheduleFlag.variationId) > 1;

	const sendUserCoordinatesToBraze = featureFlags?.featureList?.includes('sendUserCoordinatesToBraze') ?? false;

	useEffect(() => {
		if (!brazeEnabled) {
			return;
		}

		// Aborted on unmount so an in-flight verification poll cannot keep timers
		// alive or log an event for a page the visitor has already left.
		const controller = new AbortController();

		import('@src/pages/utils/braze-exports').then(
			({
				initialize,
				openSession,
				automaticallyShowInAppMessages,
				getDeviceId,
				getUser,
				logCustomEvent,
				requestImmediateDataFlush,
			}) => {
				if (controller.signal.aborted) {
					return;
				}

				initialize(brazeSdkKey, {
					baseUrl: brazeBaseUrl,
					enableLogging: brazeLoggingEnabled,
					allowUserSuppliedJavascript: true,
				});
				automaticallyShowInAppMessages();
				openSession();

				const coordinates = pageProps?.user?.coordinates;
				if (
					!sendUserCoordinatesToBraze ||
					!pageProps?.user?.hasIpBasedCoordinates ||
					!Number.isFinite(coordinates?.latitude) ||
					!Number.isFinite(coordinates?.longitude)
				) {
					return;
				}

				// `getDeviceId` can be undefined before the session is established.
				// Without it the export endpoint has no way to find this anonymous
				// profile, so verification is skipped rather than guessed at.
				const deviceId = getDeviceId();

				// A previous page view in this session already settled this exact
				// device and location; re-running would only burn Braze's export quota.
				if (hasSettledVerification(readVerificationState(), deviceId, coordinates)) {
					return;
				}

				const locationEnqueued = getUser()?.setLastKnownLocation(coordinates.latitude, coordinates.longitude);
				if (!locationEnqueued) {
					return;
				}

				const handleFlush = async (success: boolean) => {
					if (!success) {
						getLogger('client').warn({
							message: 'Braze location flush failed; verifying anyway',
							category: 'Braze',
						});
					}

					// A successful flush only means the payload left the browser. Poll
					// Braze's export endpoint until it confirms the coordinates landed,
					// so location-based rules can operate reliably.
					const outcome = deviceId
						? await verifyBrazeLocation({
								deviceId,
								latitude: coordinates.latitude,
								longitude: coordinates.longitude,
								signal: controller.signal,
						  })
						: 'skipped';

					writeVerificationState({
						deviceId: deviceId ?? '',
						latitude: coordinates.latitude,
						longitude: coordinates.longitude,
						outcome,
					});

					if (controller.signal.aborted) {
						return;
					}

					if (outcome === 'mismatch') {
						// Braze holds coordinates that are not ours. This is the one
						// definitive negative, so the downstream workflow is held back.
						getLogger('client').warn({
							message: 'Braze holds different coordinates; skipping location_resolved',
							category: 'Braze',
						});
						return;
					}

					if (outcome !== 'verified') {
						// Timeout, upstream error, throttling, or no device id. These are
						// failures of our observability path, not evidence that the
						// location failed to persist, so the event still fires and carries
						// the verification status for segmentation in Braze.
						getLogger('client').warn({
							message: `Braze location unverified (${outcome}); logging location_resolved anyway`,
							category: 'Braze',
						});
					}

					logCustomEvent('location_resolved', {
						latitude: coordinates.latitude,
						longitude: coordinates.longitude,
						location_verified: outcome === 'verified',
					});
					// Push the event out now instead of waiting for the next flush interval.
					requestImmediateDataFlush();
				};

				requestImmediateDataFlush((success) => {
					void handleFlush(success);
				});
			}
		);

		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [brazeEnabled]);

	// get  set referrer for marketing payload.
	useInitialReferrer();

	// Capture marketing_source cookie from landing URL query params.
	useMarketingSource({
		enableCcTransformation: featureFlags?.enableCcMarketingSourceTransformation ?? false,
		backfillReferralTransId: featureFlags?.enableCcMarketingSourceTransformation ?? false,
	});

	const salesforceMCPConfig = pageProps?.appData?.config?.salesforceMCP;
	useSalesforceMCP(
		salesforceMCPConfig?.enabled
			? {
					account: salesforceMCPConfig.account,
					dataset: salesforceMCPConfig.dataset,
					cookieDomain: salesforceMCPConfig.cookieDomain,
			  }
			: null
	);

	useCrestaEventManager(
		isClearChoiceBrand,
		isClearChoiceBrand
			? () => ({
					brand: brandName,
					platform: 'NextJS',
					page_origin_name:
						typeof document === 'undefined' ? router.pathname : document.title || router.pathname,
					page_origin_url: typeof globalThis.window !== 'undefined' ? globalThis.window.location.href : '',
					tag_session_id: getSessionId(),
			  })
			: undefined
	);

	useEffect(() => {
		if (isSentryEnabled()) {
			initializeSentryClient(environment);
			Sentry.setTag('brand', brandName);
			Sentry.setAttribute('brand', brandName);
			Sentry.setContext('user', {
				sessionId: getSessionId(),
				brand: brandName,
			});
		}
	}, [brandName, environment]);

	useEffect(() => {
		if (isLogRocketEnabled() && features?.logRocket?.projectId) {
			initializeLogRocket(environment, features.logRocket.projectId);
		}
	}, [brandName, environment, features?.logRocket?.projectId]);

	// Expose ClearChoice API key to window for widget access
	useEffect(() => {
		if (typeof globalThis !== 'undefined') {
			globalThis.__CLEARCHOICE_API_KEY__ = process.env.NEXT_PUBLIC_CC_API_KEY;
		}
	}, []);

	// Dev-only: warn if a page mounts the default Layout but its
	// getServerSideProps did not populate `serverFeatureFlags`. Without this
	// wiring, layout-level VWO flags (e.g. showNewHeader) silently default to
	// off on that route regardless of the experiment.
	useEffect(() => {
		if (
			process.env.NODE_ENV !== 'production' &&
			!Component.getLayout &&
			pageProps?.serverFeatureFlags === undefined
		) {
			console.warn(
				'[VWO FME] pageProps.serverFeatureFlags is undefined on a page that mounts the default Layout. ' +
					"Add `resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...)` to this page's getServerSideProps; " +
					'layout-level flags will fall back to disabled.'
			);
		}
	}, [Component, pageProps?.serverFeatureFlags]);

	useEffect(() => {
		// when component is mounting we remove server side rendered css:
		const jssStyles = document.querySelector('#jss-server-side');

		if (jssStyles && jssStyles.parentNode) {
			jssStyles.parentNode.removeChild(jssStyles);
		}

		router.events.on('routeChangeComplete', () => null);

		return () => {
			router.events.off('routeChangeComplete', () => null);
		};
	}, [router.events]);
	const getLayout =
		Component.getLayout ??
		((page) => {
			return (
				<Layout
					locale={router.locale || 'en-US'}
					preview={pageProps.preview}
					appData={pageProps.appData}
					isFooterLite={features.footer.lite}
					isFooterAlt={features.footer.alt}
					withSimpleLayout={features.footer.withSimpleLayout}
					footerData={pageProps.footerData}
					showHeaderNavMenu={featureFlags?.showHeaderNavigationMenu || false}
					breadcrumbsData={pageProps.breadcrumbsData}
					withFixedHeader={!features.header.hideHeaderOnScroll}
					disableStickyFooterOnMobile={disableStickyFooterOnMobile}
					disableStickyFooterOnDesktop={disableStickyFooterOnDesktop}
					hideLocation={featureFlags?.hideHeaderFacilityLocator ?? false}
					hidePrimaryActionButton={
						featureFlags?.hideHeaderSchedulingButton ?? pageProps.page?.hideScheduleButtonInHeader ?? false
					}
					disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
					disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
					logoHref={logoHref}
					showAspen2Header={showAspen2Header}
					showClearChoice2Header={showClearChoice2Header}
					hideNavigation={hideNavigation}
					phoneSwapOn={phoneSwapOn}
					ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
				>
					{page}
				</Layout>
			);
		});

	const dentrinoClientId =
		brandName === 'ClearChoice' ? pageProps?.env?.CC_DENTRINO_CLIENT_ID : pageProps?.env?.DENTRINO_CLIENT_ID || '';
	const dentrinoClientSecret =
		brandName === 'ClearChoice'
			? pageProps?.env?.CC_DENTRINO_CLIENT_SECRET
			: pageProps?.env?.DENTRINO_CLIENT_SECRET || '';

	// check to see if there is a config if there is no config the app cannot load.
	// if there is no config try to do a reload to get the config from the server
	// limit reloads to 1 time
	if (!config) {
		const hasPageReloaded = globalThis?.sessionStorage?.getItem('hasPageReloaded');
		if (!hasPageReloaded) {
			globalThis?.sessionStorage?.setItem('hasPageReloaded', '1');
			globalThis.location.reload();
		}
		return (
			<div>
				{' '}
				This page could not be loaded <a href="/">Return to Home</a>
			</div>
		);
	}
	globalThis?.sessionStorage?.removeItem('hasPageReloaded');

	const vwoAccountId = features?.analytics?.vwoAccountId;
	const loadVWOFromGTM = featureFlags?.featureList?.includes('loadVWOFromGTM');

	return (
		<Session>
			{features?.analytics?.enabled && !loadVWOFromGTM && vwoAccountId && (
				<VWOSmartCode accountId={vwoAccountId} />
			)}
			<ContextProviders
				config={pageProps.config}
				preview={pageProps.preview}
				environment={environment}
				user={pageProps.user}
				pageTheme={pageProps?.page?.pageTheme}
				defaultToCdn={pageProps?.config?.featureFlags?.defaultToCdn}
				dentrinoClientId={dentrinoClientId}
				dentrinoClientSecret={dentrinoClientSecret}
				deviceType={pageProps.deviceType}
				hideHeaderFacilityLocator={featureFlags?.hideHeaderFacilityLocator}
				stateCodeFromHeaders={pageProps.stateCodeFromHeaders}
				phoneSwapOn={phoneSwapOn}
			>
				{getLayout(<Component {...pageProps} />)}
			</ContextProviders>
		</Session>
	);
};

export default appWithTranslation(App);
